import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router'; // 👈 1. ADDED ROUTER IMPORTS
import { DashboardService, Order, Product, Expense, MerchantProfile, SubscriptionStatusResponse } from './dashboard.service';
import { BUSINESS_ID_KEY, SESSION_TOKEN_KEY } from '../../auth.interceptor';

interface ParsedSaleData {
  productName: string;
  productPrice: number;
  quantity: number;
  address: string;
}

export type ModalState = 'NONE' | 'GENERATE_LINK' | 'ADD_PRODUCT' | 'EDIT_PRODUCT' | 'ADD_EXPENSE' | 'EDIT_EXPENSE' | 'EDIT_ORDER' | 'ADD_ORDER' | 'EDIT_PROFILE';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard implements OnInit {

  activeTab: 'orders' | 'products' | 'expenses' | 'profile' = 'orders';

  // Central modal state properties
  currentModal: ModalState = 'NONE';

  newProduct: any = { name: '', price: null, sku: '' };
  newExpense = { itemName: '', amount: null };

  showModal = false;
  isCopied: boolean = false;
  saleData: string = '';
  orders: Order[] = [];
  orderCounter: number = 0;
  products: Product[] = [];
  currentBusinessId: string = '60173826674';
  expenses: any[] = [];
  currentPage: number = 1;
  pageSize: number = 5; // Change this to how many items you want per page
  selectedMonth: string = '';
  selectedStatus: string = '';
  subscriptionFee: number = 50;
  isSubscriber: string = '0';
  paidUntil: string | null = null;
  totalOrdersCount: number = 0;

  merchantProfile: MerchantProfile | null = null;
  showBankAccount = false;
  editProfileData: MerchantProfile = {
    businessId: '',
    businessName: '',
    bankName: '',
    bankAccount: ''
  };

  // 🌟 2. ADDED AUTHENTICATION STATE PROPERTIES HERE
  businessId: string = '';
  isAuthenticated: boolean = false;
  selectedProductId: any = "";
  selectedItem: any = { items: [] };

  constructor(
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute, // 👈 3. INJECTED ACTIVATED ROUTER
    private router: Router         // 👈 4. INJECTED CORE ROUTER
  ) { }

  // 5. CONSOLIDATED AND COMBINED BOTH ngOnInit LIFECYCLES INTO ONE
  ngOnInit(): void {
    // A. First catch query parameters for Magic Link security verification
    this.route.queryParams.subscribe(params => {
      const token = params['token'];

      if (token) {
        this.verifyMagicToken(token);
      } else {
        // Fallback check if your dad opens the dashboard app normally without a token string link
        this.checkExistingSession();
      }
    });
  }

  //Development
  /*ngOnInit(): void {

    localStorage.clear();

    // Override: Bypass token checking and load a hardcoded/local session directly
    const forcedBusinessId = '';

    if (forcedBusinessId) {
      this.businessId = forcedBusinessId;
      this.currentBusinessId = forcedBusinessId;
      this.isAuthenticated = true;
      localStorage.setItem('notabayar_business_id', forcedBusinessId);
      this.loadDashboardData();
    } else {
      this.checkExistingSession();
    }
  }*/


  verifyMagicToken(token: string): void {
    this.dashboardService.verifyToken(token).subscribe({
      next: (res: any) => {
        this.businessId = res.businessId;
        this.currentBusinessId = res.businessId; // Sets the scope context for your active data fetchers
        this.isAuthenticated = true;

        // Clean the URL immediately so your dad doesn't bookmark the single-use raw token
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { token: null },
          queryParamsHandling: 'merge'
        });

        // Save session locally so refreshing the web browser doesn't log him out out-of-the-blue.
        // The session token is what the backend checks; the businessId is only used to build URLs.
        localStorage.setItem(SESSION_TOKEN_KEY, res.sessionToken);
        localStorage.setItem(BUSINESS_ID_KEY, this.businessId);

        // Now run the operational data retrieval loops smoothly
        this.loadDashboardData();
      },
      error: (err) => {
        this.isAuthenticated = false;
        console.error('Magic link validation stream failed:', err);
        alert('Link telah tamat tempoh. Sila dapatkan link baru di WhatsApp.');
      }
    });
  }

  checkExistingSession(): void {
    const savedId = localStorage.getItem(BUSINESS_ID_KEY);
    const savedToken = localStorage.getItem(SESSION_TOKEN_KEY);
    if (savedId && savedToken) {
      this.businessId = savedId;
      this.currentBusinessId = savedId;
      this.isAuthenticated = true;
      this.loadDashboardData();
    } else {
      this.isAuthenticated = false;
      // Optional: Redirect him to a clean login placeholder state or view error message layout here
    }
  }

  // 6. INTEGRATED DATA LOADER TRIGGERS INSIDE ONE METHOD AS EXPECTED BY SESSIONS
  loadDashboardData(): void {
    this.loadSubscriptionStatus();
    this.loadOrders();
    this.loadProducts();
    this.loadExpenses();
  }

  switchTab(tab: 'orders' | 'products' | 'expenses' | 'profile'): void {
    this.activeTab = tab;

    if (tab === 'products') {
      this.loadProducts();
    } else if (tab === 'orders') {
      this.loadOrders();
    } else if (tab === 'expenses') {
      this.loadExpenses();
    } else if (tab === 'profile') {
      this.loadMerchantProfile();
    }

    this.cdr.detectChanges();
    console.log('Switched to tab:', this.activeTab);
  }

  loadOrders(): void {
    this.dashboardService.getAllOrders(this.currentBusinessId).subscribe({
      next: (data: any[]) => {
        const groupedMap = new Map();
        data.forEach(order => {
          if (!groupedMap.has(order.orderId)) {
            groupedMap.set(order.orderId, {
              ...order,
              items: []
            });
          }
          const existing = groupedMap.get(order.orderId);
          existing.items.push({
            productName: order.productDetails,
            productPrice: order.productPrice,
            quantity: order.quantity
          });
        });
        this.orders = Array.from(groupedMap.values());
        this.cdr.detectChanges();
      }
    });
  }

  loadProducts(): void {
    this.dashboardService.getProductsByBusiness(this.currentBusinessId).subscribe({
      next: (response: Product[]) => {
        this.products = response || [];
        
        // 🚀 Auto-switch to products tab if the merchant has no products yet
        if (this.products.length === 0) {
          this.activeTab = 'products';
        }

        this.cdr.detectChanges();
        console.log('Products loaded successfully:', this.products);
      },
      error: (error) => {
        console.error('Failed to read catalog records:', error);
      }
    });
  }

  private loadExpenses(): void {
    console.log('Fetching active operating expenses from API pipeline for business:', this.currentBusinessId);
    this.dashboardService.getExpensesByBusiness(this.currentBusinessId).subscribe({
      next: (response: Expense[]) => {
        this.expenses = response || [];
        this.cdr.detectChanges();
        console.log('Expenses loaded successfully:', this.expenses);
      },
      error: (error) => {
        console.error('Failed to read live cost parameters from API stream:', error);
      }
    });
  }

  // ==========================================
  // 🏢 MERCHANT PROFILE MODAL & LOGIC
  // ==========================================

  loadMerchantProfile(): void {
    this.dashboardService.getMerchantProfile(this.currentBusinessId).subscribe({
      next: (profile: MerchantProfile) => {
        this.merchantProfile = profile;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.warn('No existing merchant profile found or failed loading:', err);
      }
    });
  }

  openEditProfileModal(): void {
    // Populate form with current values or defaults if empty
    this.editProfileData = this.merchantProfile ? { ...this.merchantProfile } : {
      businessId: this.currentBusinessId,
      businessName: '',
      bankName: '',
      bankAccount: ''
    };
    this.currentModal = 'EDIT_PROFILE';
    this.showModal = true;
  }

  toggleBankAccountVisibility(): void {
    this.showBankAccount = !this.showBankAccount;
  }

  maskBankAccount(bankAccount: string): string {
    return '•'.repeat(bankAccount.length);
  }

  saveProfileUpdate(): void {
    if (!this.editProfileData.businessName || !this.editProfileData.bankName || !this.editProfileData.bankAccount) {
      alert('Please complete business name, bank name and bank account number.');
      return;
    }

    this.dashboardService.saveOrUpdateMerchantProfile(this.currentBusinessId, this.editProfileData).subscribe({
      next: (updatedProfile) => {
        this.merchantProfile = updatedProfile;
        alert('Business profile and bank information successfully updated');
        this.loadMerchantProfile();
        this.closeModal();
      },
      error: (err) => {
        console.error('Failed updating merchant profile:', err);
        alert('Failed updating merchant profile');
      }
    });
  }

  // ==========================================
  // 📦 ACTIVE ORDERS 
  // ==========================================

  calculateOrderTotal(order: any): number {
    if (order.items && order.items.length > 0) {
      return order.items.reduce((sum: number, item: any) => {
        const price = item.productPrice || order.productPrice || 0;
        const qty = item.quantity || 1;
        return sum + (price * qty);
      }, 0);
    }
    return (order.productPrice || 0) * (order.quantity || 1);
  }

  // Getter that filters your orders array based on the selected month
  // Combined getter that filters your orders array based on selected month AND payment status
  get filteredOrders() {
    return this.orders.filter(order => {
      // 1. Month Filter Check
      if (this.selectedMonth) {
        if (!order.createdAt) {
          return false;
        }
        const orderDate = new Date(order.createdAt);
        if (isNaN(orderDate.getTime())) {
          return false;
        }
        const monthString = String(orderDate.getMonth() + 1).padStart(2, '0');
        if (monthString !== this.selectedMonth) {
          return false;
        }
      }

      // 2. Status Filter Check
      if (this.selectedStatus) {
        const orderStatus = order.paymentStatus ? order.paymentStatus.toUpperCase() : 'PENDING';
        if (orderStatus !== this.selectedStatus.toUpperCase()) {
          return false;
        }
      }

      return true;
    });
  }

  // Trigger to open the empty form
  openAddOrderModal(): void {
    this.selectedItem = {
      items: [{ productId: '', quantity: 1 }], // Initialize with 1 empty row
      customerName: '',
      customerPhoneNumber: '',
      customerAddress: ''
    };
    this.currentModal = 'ADD_ORDER';
    this.showModal = true;
  }

  // Submit the new order
  submitAddOrder(): void {
    const payload = {
      businessId: this.businessId || this.currentBusinessId,
      items: this.selectedItem.items.map((i: any) => ({
        productId: Number(i.productId),
        quantity: Number(i.quantity)
      })),
      customerName: this.selectedItem.customerName,
      customerPhoneNumber: this.selectedItem.customerPhoneNumber,
      customerAddress: this.selectedItem.customerAddress,
      paymentStatus: 'PENDING'
    };

    this.dashboardService.createOrder(payload).subscribe({
      next: (responseOrders) => {
        alert('Order added successfully!');
        this.loadOrders();
        this.closeModal();
        this.cdr.detectChanges();
        console.log("Current Modal" + this.currentModal);
        console.log("Current selectedItem" + this.selectedItem);
        console.log("Current showModal" + this.showModal);
        // this.currentModal = 'NONE';
        // this.selectedItem = null;
        // this.showModal = false;
      },
      error: (err) => {
        console.error('Failed adding order:', err);
        alert('Failed to add order.');
      }
    });
  }

  addOrderItemRow(): void {
    if (!this.selectedItem.items) {
      this.selectedItem.items = [];
    }
    this.selectedItem.items.push({ productId: '', quantity: 1 });
  }

  removeOrderItemRow(index: number): void {
    if (this.selectedItem.items && this.selectedItem.items.length > 1) {
      this.selectedItem.items.splice(index, 1);
    }
  }

  markAsPaid(orderId: any): void {
    console.log(`💰 [NotaBayar UI] Changing Payment Status for Order ID:`, orderId);

    this.dashboardService.updatePaymentStatus(orderId, 'PAID').subscribe({
      next: (updatedOrders) => {
        // Loop through all orders in your local array and update any that match this orderId
        let updatedCount = 0;
        this.orders.forEach(o => {
          if (o.orderId === orderId) {
            o.paymentStatus = 'PAID';
            updatedCount++;
          }
        });

        this.cdr.detectChanges();
        console.log(`✓ Updated ${updatedCount} row(s) for Order ID ${orderId} to PAID.`);
      },
      error: (error) => {
        console.error('Failed to update tracking status parameters on backend server:', error);
        alert('Fail to update payment status. Try again');
      }
    });
  }

  editOrder(orderId: any): void {
    const targetOrder = this.orders.find(o => o.id === orderId);
    if (targetOrder) {
      console.log('products at edit time:', this.products);
      console.log('raw targetOrder.items:', JSON.stringify((targetOrder as any).items));
      this.selectedItem = {
        id: targetOrder.id,
        customerName: targetOrder.customerName,
        customerPhoneNumber: targetOrder.customerPhoneNumber,
        customerAddress: targetOrder.customerAddress,
        paymentStatus: targetOrder.paymentStatus,
        items: (targetOrder as any).items && (targetOrder as any).items.length > 0
          ? (targetOrder as any).items.map((item: any) => ({
            productId: this.products.find(p => p.name === item.productName)?.id ?? '',
            quantity: item.quantity
          }))
          : [{
            productId: this.products.find(p => p.name === targetOrder.productDetails)?.id ?? '',
            quantity: targetOrder.quantity
          }]
      };
      console.log('selectedItem.items:', JSON.stringify(this.selectedItem.items));
      console.log('productId type:', typeof this.selectedItem.items[0]?.productId);
      console.log('targetOrder.productDetails:', JSON.stringify(targetOrder.productDetails));
      console.log('products names:', this.products.map(p => p.name));
      this.currentModal = 'EDIT_ORDER';
    }
  }

  saveOrderUpdate(): void {
    const payload = {
      businessId: this.currentBusinessId,
      items: this.selectedItem.items.map((i: any) => ({
        productId: Number(i.productId),
        quantity: Number(i.quantity)
      })),
      customerName: this.selectedItem.customerName,
      customerPhoneNumber: this.selectedItem.customerPhoneNumber,
      customerAddress: this.selectedItem.customerAddress,
      paymentStatus: this.selectedItem.paymentStatus || 'PENDING'
    };

    this.dashboardService.updateOrder(this.selectedItem.id, payload).subscribe({
      next: (responseOrders) => {
        alert('Order updated successfully!');
        this.loadOrders();
        this.closeModal();
        console.log("Current Modal" + this.currentModal);
        console.log("Current selectedItem" + this.selectedItem);
        console.log("Current showModal" + this.showModal);
        // this.currentModal = 'NONE';
        // this.selectedItem = null;
        // this.showModal = false;
      },
      error: (err) => {
        console.error('Failed to update order:', err);
        alert('Failed to update order.');
      }
    });
  }

  deleteOrder(id: any): void {
    const confirmed = confirm('Are you sure you want to delete this order?');
    if (!confirmed) return;

    this.dashboardService.deleteOrder(id).subscribe({
      next: () => {
        console.log(`Order ${id} deleted`);
        alert('Order deleted successfully!');
        this.loadOrders();
        this.closeModal();
      },
      error: (error) => {
        console.error('Delete failed:', error);
        alert('Failed to delete order.');
      }
    });
  }

  /*downloadInvoice(order: any): void {
    this.dashboardService.generateInvoice([order]).subscribe({
      next: async (blob) => {
        // 1. Create a File object from the blob
        const fileName = `Invois_${order.id}.pdf`;
        const file = new File([blob], fileName, { type: 'application/pdf' });

        // 2. Check if the device supports native sharing
        if (navigator.share && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Invois Jualan',
              text: 'Berikut adalah invois untuk pesanan anda.'
            });
          } catch (err) {
            console.error('Share failed', err);
            // Fallback if user cancels or error
            this.triggerFallbackDownload(blob, fileName);
          }
        } else {
          // Fallback for browsers that don't support file sharing
          this.triggerFallbackDownload(blob, fileName);
        }
      },
      error: (err) => alert('Gagal menjana invois.')
    });
  } */

  downloadInvoice(order: any): void {
    this.dashboardService.generateInvoice([order]).subscribe({
      next: async (blob) => {
        // Open the PDF directly in a new browser tab
        window.open(URL.createObjectURL(blob), '_blank');

        // 1. Create a File object from the blob
        const fileName = `Invois_${order.id}.pdf`;
        const file = new File([blob], fileName, { type: 'application/pdf' });

        // 2. Check if the device supports native sharing
        if (navigator.share && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Invois Jualan',
              text: 'Berikut adalah invois untuk pesanan anda.'
            });
          } catch (err) {
            console.error('Share failed', err);
            // Fallback if user cancels or error
            this.triggerFallbackDownload(blob, fileName);
          }
        } else {
          // Fallback for browsers that don't support file sharing
          this.triggerFallbackDownload(blob, fileName);
        }
      },
      error: (err) => alert('Fail to generate invoice.')
    });
  }

  /* downloadReceipt(order: any): void {
    this.dashboardService.generateReceipt([order]).subscribe({
      next: async (blob) => {
        // 1. Create a File object from the blob with 'Resit' prefix
        const fileName = `Resit_${order.id}.pdf`;
        const file = new File([blob], fileName, { type: 'application/pdf' });

        // 2. Check if the device supports native sharing
        if (navigator.share && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Resit Rasmi Pembayaran',
              text: 'Berikut adalah resit pembayaran untuk pesanan anda.'
            });
          } catch (err) {
            console.error('Share failed', err);
            this.triggerFallbackDownload(blob, fileName);
          }
        } else {
          this.triggerFallbackDownload(blob, fileName);
        }
      },
      error: (err) => alert('Gagal menjana resit.')
    });
  } */

  downloadReceipt(order: any): void {
    this.dashboardService.generateReceipt([order]).subscribe({
      next: async (blob) => {
        // Open the receipt directly in a new browser tab
        window.open(URL.createObjectURL(blob), '_blank');

        // 1. Create a File object from the blob with 'Resit' prefix
        const fileName = `Resit_${order.id}.pdf`;
        const file = new File([blob], fileName, { type: 'application/pdf' });

        // 2. Check if the device supports native sharing
        if (navigator.share && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Resit Rasmi Pembayaran',
              text: 'Berikut adalah resit pembayaran untuk pesanan anda.'
            });
          } catch (err) {
            console.error('Share failed', err);
            this.triggerFallbackDownload(blob, fileName);
          }
        } else {
          this.triggerFallbackDownload(blob, fileName);
        }
      },
      error: (err) => alert('Fail to generate receipt.')
    });
  }

  private triggerFallbackDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }


  // ==========================================
  // 🏷️ PRODUCT CATALOG MODAL LOGIC
  // ==========================================

  openAddProductModal(): void {
    this.newProduct = { name: '', price: null, sku: '', businessId: this.currentBusinessId };
    this.currentModal = 'ADD_PRODUCT';
  }

  getProductDropdownOptions(): Product[] {
    return this.products;
  }

  onProductSelect(productId: any): void {
    // Use the ID directly since ngModel passed it to the method
    const selectedProduct = this.products.find(p => p.id == productId);

    if (selectedProduct) {
      this.selectedItem.productDetails = selectedProduct.name;
      // Optional: sync price if you want
      this.selectedItem.productPrice = selectedProduct.price;
    }
  }

  submitAddProduct(): void {
    if (!this.newProduct.name || !this.newProduct.price) {
      alert('Please fill in product details.');
      return;
    }

    this.dashboardService.createProduct(this.newProduct).subscribe({
      next: (savedProduct) => {
        this.products.unshift(savedProduct);
        this.loadProducts();
        this.closeModal();
      },
      error: (err) => alert('Fail to add Product. Try again')
    });
  }

  editProduct(productId: any): void {
    const targetProduct = this.products.find(p => p.id === productId);
    if (targetProduct) {
      this.selectedItem = { ...targetProduct };
      this.currentModal = 'EDIT_PRODUCT';
    }
  }

  saveProductUpdate(): void {
    if (!this.selectedItem.name || !this.selectedItem.price) {
      alert('Please fill in product name and product price.');
      return;
    }

    this.dashboardService.updateProduct(this.selectedItem.id, this.selectedItem).subscribe({
      next: (updatedProduct) => {
        const index = this.products.findIndex(p => p.id === updatedProduct.id);
        if (index !== -1) {
          this.products[index] = updatedProduct;
        }
        alert('Product record updated successfully!');
        this.loadProducts();
        this.closeModal();
      },
      error: (err) => alert('Failed to update product details.')
    });
  }

  deleteProduct(productId: any): void {
    if (confirm('Are you sure you want to delete this product?')) {
      this.dashboardService.deleteProduct(productId).subscribe({
        next: () => {
          this.products = this.products.filter(p => p.id !== productId);
          alert('Product entry removed successfully!');
          this.loadProducts();
        },
        error: (err) => alert('Fail to delete product details.')
      });
    }
  }

  // ==========================================
  // 💸 EXPENSES TRACKER MODAL LOGIC
  // ==========================================

  openAddExpenseModal(): void {
    this.newExpense = { itemName: '', amount: null };
    this.currentModal = 'ADD_EXPENSE';
  }

  editExpense(expenseId: any): void {
    const targetExpense = this.expenses.find(e => e.id === expenseId);
    if (targetExpense) {
      this.selectedItem = { ...targetExpense };
      this.currentModal = 'EDIT_EXPENSE';
    } else {
      console.error(`Expense record could not be mapped with ID: ${expenseId}`);
    }
  }

  submitAddExpense(): void {
    if (!this.newExpense.itemName || !this.newExpense.amount) {
      alert('Please supply an Expense Name and Amount.');
      return;
    }

    const payload: Expense = {
      businessId: this.currentBusinessId,
      itemName: this.newExpense.itemName,
      amount: Number(this.newExpense.amount)
    };

    this.dashboardService.createExpense(payload).subscribe({
      next: () => {
        alert('Expense recorded successfully.');
        this.loadExpenses();
        this.closeModal();
      },
      error: (err) => console.error('Failed writing new manual asset expense record:', err)
    });
  }

  saveExpenseUpdate(): void {
    if (!this.selectedItem.itemName || !this.selectedItem.amount) {
      alert('Item Name and Amount cannot be empty.');
      return;
    }

    this.dashboardService.updateExpense(this.selectedItem.id, this.selectedItem).subscribe({
      next: () => {
        alert('Expense record updated successfully!');
        this.loadExpenses();
        this.closeModal();
      },
      error: (err) => console.error('Failed mapping modifications update:', err)
    });
  }

  deleteExpense(expenseId: any): void {
    const confirmed = confirm('Are you sure you want to delete this expense record?');
    if (!confirmed) return;

    this.dashboardService.deleteExpense(expenseId).subscribe({
      next: () => {
        alert('Expense entry removed successfully!');
        this.loadExpenses();
      },
      error: (err) => console.error('Failed processing delete pipeline request:', err)
    });
  }

  closeModal(): void {
    this.currentModal = 'NONE';
    this.selectedItem = null;
    this.showModal = false;
  }

  openModal() {
    this.currentModal = 'GENERATE_LINK';
    this.showModal = true;
  }

  // ==========================================
  // UTILITY OPERATIONS HANDLERS
  // ==========================================

  submitSaleData(): void {
    if (!this.saleData.trim()) return;

    this.dashboardService.submitSaleData(this.saleData, this.currentBusinessId).subscribe({
      next: (response: any) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.loadOrders();
          this.saleData = '';
          alert(`${response.data.length} orders saved successfully!`);
        }
      },
      error: (error) => {
        console.error('HTTP Error:', error);
        alert('Failed to submit sale data.');
      }
    });
  }

  copyLink(urlValue: string): void {
    navigator.clipboard.writeText(urlValue)
      .then(() => {
        this.isCopied = true;
        setTimeout(() => this.isCopied = false, 2000);
        alert('Link copied to clipboard!');
      })
      .catch(err => console.log('Could not copy text: ', err));
  }

  onMerchantQrSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      console.log('New merchant QR selected:', file.name);
    }
  }

  loadSubscriptionStatus(): void {
    this.dashboardService.getSubscriptionStatus(this.currentBusinessId).subscribe({
      next: (response: SubscriptionStatusResponse) => {
        
        this.subscriptionFee = response.currentFee;
        this.isSubscriber = response.isSubscriber;
        this.paidUntil = response.paidUntil || null;
        this.totalOrdersCount = response.totalOrders;
        console.log(this.subscriptionFee);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load subscription status parameters:', err);
      }
    });
  }

  // Update your getter to handle both month filtering AND pagination slicing
  get paginatedOrders() {
    const filtered = this.filteredOrders; // Uses your existing month-filtered getter
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredOrders.length / this.pageSize) || 1;
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }
}